import { useCallback, useRef, useState } from 'react';
import BackendService from '../../../services/admin/BackendService';
import { InvestorStatementItem } from '../../../models/admin/InvestorStatement';
import { useStatementConfirmAndSendActions } from './useStatementConfirmAndSendActions';
import { useStatementViewAndSkipActions } from './useStatementViewAndSkipActions';

type ToastFn = (message: string, type: 'error' | 'warning' | 'info' | 'success') => void;

/**
 * View/confirm/skip actions for the statement review list, plus the confirm -> send-prompt
 * sequencing. Thin orchestrator over useStatementViewAndSkipActions and
 * useStatementConfirmAndSendActions — owns only what both share (the backend
 * instance, the busy-item flag, and the single updateItem writer).
 */
export function useStatementReviewActions(
  items: InvestorStatementItem[],
  onItemsChange: (items: InvestorStatementItem[]) => void,
  onShowToast: ToastFn,
) {
  const backendRef = useRef(new BackendService());
  const [busyItemId, setBusyItemId] = useState<string | null>(null);

  const updateItem = useCallback(
    (externalId: string, patch: Partial<InvestorStatementItem>) => {
      onItemsChange(
        items.map((item) => (item.externalId === externalId ? { ...item, ...patch } : item)),
      );
    },
    [items, onItemsChange],
  );

  const { handleView, handleSkip } = useStatementViewAndSkipActions(
    backendRef,
    updateItem,
    setBusyItemId,
    onShowToast,
  );

  const confirmAndSend = useStatementConfirmAndSendActions(
    items,
    backendRef,
    updateItem,
    setBusyItemId,
    onShowToast,
  );

  return {
    busyItemId,
    handleView,
    handleSkip,
    ...confirmAndSend,
  };
}
